import { useEffect, useState } from "react";
import api, { fmt, fmtNum, API } from "@/lib/api";
import { BarChart3, TrendingUp, Users, Package, Layers, PiggyBank, Download, Calendar, Scale } from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { id: "tax", label: "Tax", icon: BarChart3 },
    { id: "items", label: "Items", icon: Package },
    { id: "categories", label: "Categories", icon: Layers },
    { id: "clients", label: "Clients", icon: Users },
    { id: "trend", label: "Trend", icon: TrendingUp },
    { id: "savings", label: "Savings", icon: PiggyBank },
    { id: "salesmix", label: "B2B vs B2C", icon: Scale },
];

function firstOfMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function today() {
    return new Date().toISOString().slice(0, 10);
}

export default function Reports() {
    const [tab, setTab] = useState("tax");
    const [start, setStart] = useState(firstOfMonth());
    const [end, setEnd] = useState(today());
    const [item, setItem] = useState("");
    const [bucket, setBucket] = useState("month");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        setData(null); // clear stale shape before fetching new tab
        try {
            const params = { start, end };
            if (tab === "items") params.item = item;
            if (tab === "trend") params.bucket = bucket;
            const apiPath = tab === "salesmix" ? "sales-mix" : tab;
            const r = await api.get(`/reports/${apiPath}`, { params });
            setData(r.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to load report"); }
        setLoading(false);
    };
    useEffect(() => { load(); }, [tab, start, end, bucket]);
    // Item filter is applied on-demand via the "Apply" button; auto-load when cleared.
    useEffect(() => { if (tab === "items" && !item) load(); }, [item, tab]);

    const exportUrl = () => {
        const p = new URLSearchParams({ start, end });
        if (tab === "items" && item) p.set("item", item);
        if (tab === "trend") p.set("bucket", bucket);
        return `${API}/reports/export/${tab}.xlsx?${p.toString()}`;
    };

    return (
        <div data-testid="reports-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Reports</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Tax, sales, savings & trend analytics. Cancelled invoices are excluded from every metric.
                    </p>
                </div>
                <a href={exportUrl()} className="hdb-btn hdb-btn-primary" data-testid="export-report-btn">
                    <Download size={14} /> Export {tab.toUpperCase()} (Excel)
                </a>
            </div>

            {/* Filters */}
            <div className="hdb-card p-4 mb-5 flex flex-wrap gap-4 items-end">
                <div>
                    <label className="hdb-label"><Calendar size={11} className="inline mr-1" /> From</label>
                    <input type="date" className="hdb-input hdb-mono" value={start}
                           onChange={(e) => setStart(e.target.value)} data-testid="report-start-date" />
                </div>
                <div>
                    <label className="hdb-label">To</label>
                    <input type="date" className="hdb-input hdb-mono" value={end}
                           onChange={(e) => setEnd(e.target.value)} data-testid="report-end-date" />
                </div>
                {tab === "items" && (
                    <div className="min-w-[260px]">
                        <label className="hdb-label">Filter by item name</label>
                        <div className="flex gap-2">
                            <input className="hdb-input" placeholder="e.g. Atta, Rice, Colgate…"
                                   value={item} onChange={(e) => setItem(e.target.value)}
                                   onKeyDown={(e) => e.key === "Enter" && load()}
                                   data-testid="report-item-filter" />
                            <button className="hdb-btn hdb-btn-ghost" onClick={load}>Apply</button>
                        </div>
                    </div>
                )}
                {tab === "trend" && (
                    <div>
                        <label className="hdb-label">Bucket</label>
                        <select className="hdb-input" value={bucket} onChange={(e) => setBucket(e.target.value)}
                                data-testid="report-bucket">
                            <option value="month">Monthly</option>
                            <option value="week">Weekly</option>
                        </select>
                    </div>
                )}
                <div className="flex flex-wrap gap-2 ml-auto">
                    {[
                        ["This month", firstOfMonth(), today()],
                        ["Last 30 days", new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10), today()],
                        ["This FY", (() => { const d = new Date(); const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; return `${y}-04-01`; })(), today()],
                        ["All time", "", today()],
                    ].map(([label, s, e]) => (
                        <button key={label} className="hdb-btn hdb-btn-ghost text-xs"
                                onClick={() => { setStart(s); setEnd(e); }}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab strip */}
            <div className="hdb-card mb-5 overflow-hidden">
                <div className="flex overflow-x-auto">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = t.id === tab;
                        return (
                            <button key={t.id} onClick={() => setTab(t.id)}
                                    data-testid={`tab-${t.id}`}
                                    className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
                                        active ? "border-slate-900 text-slate-900 bg-slate-50"
                                               : "border-transparent text-slate-500 hover:text-slate-900"
                                    }`}>
                                <Icon size={14} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Content */}
            <div className="hdb-card p-5">
                {loading && <div className="text-slate-400 text-sm">Loading…</div>}
                {!loading && data && (
                    <>
                        {tab === "tax" && <TaxView data={data} />}
                        {tab === "items" && <ItemsView data={data} />}
                        {tab === "categories" && <CategoriesView data={data} />}
                        {tab === "clients" && <ClientsView data={data} />}
                        {tab === "trend" && <TrendView data={data} bucket={bucket} />}
                        {tab === "savings" && <SavingsView data={data} />}
                        {tab === "salesmix" && <SalesMixView data={data} />}
                    </>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, tone = "slate" }) {
    const tones = { slate: "text-slate-900", mint: "text-emerald-700", gold: "text-amber-700", red: "text-red-700" };
    return (
        <div className="hdb-card p-4">
            <div className="hdb-label">{label}</div>
            <div className={`hdb-num text-2xl font-black mt-1 ${tones[tone]}`}>{value}</div>
        </div>
    );
}

function TaxView({ data }) {
    const slabs = data?.by_slab ?? [];
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Stat label="Invoices" value={data?.invoice_count ?? 0} />
                <Stat label="Total Taxable" value={fmt(data?.total_taxable ?? 0)} />
                <Stat label="Total Tax" value={fmt(data?.total_tax ?? 0)} tone="gold" />
                <Stat label="Grand Total" value={fmt(data?.total_grand ?? 0)} tone="mint" />
            </div>
            <div className="overflow-x-auto">
                <table className="hdb-table w-full">
                    <thead><tr><th>GST Rate</th><th className="text-right">Taxable</th>
                        <th className="text-right">CGST</th><th className="text-right">SGST</th>
                        <th className="text-right">IGST</th><th className="text-right">Total Tax</th></tr></thead>
                    <tbody>
                        {slabs.map((s) => (
                            <tr key={s.rate} data-testid={`tax-slab-${s.rate}`}>
                                <td className="hdb-mono font-semibold">{s.rate}%</td>
                                <td className="hdb-num hdb-num-right">{fmt(s.taxable)}</td>
                                <td className="hdb-num hdb-num-right">{fmt(s.cgst)}</td>
                                <td className="hdb-num hdb-num-right">{fmt(s.sgst)}</td>
                                <td className="hdb-num hdb-num-right">{fmt(s.igst)}</td>
                                <td className="hdb-num hdb-num-right font-bold">{fmt(s.total_tax)}</td>
                            </tr>
                        ))}
                        {slabs.length === 0 && (
                            <tr><td colSpan={6} className="text-center py-8 text-slate-400">No invoices in this range</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ItemsView({ data }) {
    const rows = data?.rows ?? [];
    const totals = data?.totals ?? { qty: 0, revenue: 0, tax: 0, bachat: 0 };
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Stat label="Items shown" value={rows.length} />
                <Stat label="Total Qty" value={fmtNum(totals.qty)} />
                <Stat label="Total Revenue" value={fmt(totals.revenue)} tone="mint" />
                <Stat label="Total Bachat given" value={fmt(totals.bachat)} tone="gold" />
            </div>
            <div className="overflow-x-auto">
                <table className="hdb-table w-full">
                    <thead><tr><th>Item</th><th className="text-right">Qty</th>
                        <th className="text-right">Revenue</th><th className="text-right">Tax</th>
                        <th className="text-right">Bachat</th><th className="text-right">Invoices</th>
                        <th className="text-right">Avg Rate</th></tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.name}>
                                <td className="font-medium">{r.name}</td>
                                <td className="hdb-num hdb-num-right">{fmtNum(r.qty)}</td>
                                <td className="hdb-num hdb-num-right font-bold">{fmt(r.revenue)}</td>
                                <td className="hdb-num hdb-num-right">{fmt(r.tax)}</td>
                                <td className="hdb-num hdb-num-right text-emerald-700">{fmt(r.bachat)}</td>
                                <td className="hdb-num hdb-num-right">{r.invoice_count}</td>
                                <td className="hdb-num hdb-num-right">{fmt(r.avg_rate)}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-8 text-slate-400">No matching lines</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CategoriesView({ data }) {
    const rows = data?.rows ?? [];
    const grandRev = rows.reduce((s, r) => s + r.revenue, 0) || 1;
    return (
        <div className="overflow-x-auto">
            <table className="hdb-table w-full">
                <thead><tr><th>Category</th><th className="text-right">Qty</th>
                    <th className="text-right">Revenue</th><th>Share</th>
                    <th className="text-right">Tax</th><th className="text-right">Bachat</th></tr></thead>
                <tbody>
                    {rows.map((r) => {
                        const share = (r.revenue / grandRev) * 100;
                        return (
                            <tr key={r.category}>
                                <td className="font-medium">{r.category}</td>
                                <td className="hdb-num hdb-num-right">{fmtNum(r.qty)}</td>
                                <td className="hdb-num hdb-num-right font-bold">{fmt(r.revenue)}</td>
                                <td>
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, share)}%` }}></div>
                                        </div>
                                        <span className="text-xs hdb-mono text-slate-500 w-12 text-right">{share.toFixed(1)}%</span>
                                    </div>
                                </td>
                                <td className="hdb-num hdb-num-right">{fmt(r.tax)}</td>
                                <td className="hdb-num hdb-num-right text-emerald-700">{fmt(r.bachat)}</td>
                            </tr>
                        );
                    })}
                    {rows.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-8 text-slate-400">No data</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function ClientsView({ data }) {
    const rows = data?.rows ?? [];
    return (
        <div className="overflow-x-auto">
            <table className="hdb-table w-full">
                <thead><tr><th>Client</th><th>GSTIN</th><th>Type</th>
                    <th className="text-right">Revenue</th><th className="text-right">Tax</th>
                    <th className="text-right">Bachat given</th><th className="text-right">Invoices</th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.name}>
                            <td className="font-medium">{r.name}</td>
                            <td className="hdb-mono text-xs">{r.gstin || "—"}</td>
                            <td><span className={`hdb-tag ${r.last_type === "B2B" ? "hdb-tag-slate" : "hdb-tag-mint"}`}>{r.last_type}</span></td>
                            <td className="hdb-num hdb-num-right font-bold">{fmt(r.revenue)}</td>
                            <td className="hdb-num hdb-num-right">{fmt(r.tax)}</td>
                            <td className="hdb-num hdb-num-right text-emerald-700">{fmt(r.bachat)}</td>
                            <td className="hdb-num hdb-num-right">{r.invoice_count}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr><td colSpan={7} className="text-center py-8 text-slate-400">No clients in this range</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function TrendView({ data, bucket }) {
    const rows = data?.rows ?? [];
    const maxRev = Math.max(...rows.map((r) => r.revenue), 1);
    return (
        <div className="space-y-5">
            <div className="hdb-card p-4">
                <div className="hdb-label mb-3">Revenue by {bucket}</div>
                <div className="space-y-2">
                    {rows.map((r) => (
                        <div key={r.bucket} className="flex items-center gap-3">
                            <div className="text-xs hdb-mono text-slate-500 w-24">{r.bucket}</div>
                            <div className="flex-1 h-6 bg-slate-50 rounded overflow-hidden relative">
                                <div className="h-full bg-slate-900" style={{ width: `${(r.revenue / maxRev) * 100}%` }}></div>
                                <div className="absolute inset-0 flex items-center px-2 text-xs font-bold text-white">
                                    {fmt(r.revenue)}
                                </div>
                            </div>
                            <div className="text-xs text-slate-500 w-16 text-right">{r.invoice_count} inv</div>
                        </div>
                    ))}
                    {rows.length === 0 && (
                        <div className="text-center py-8 text-slate-400">No data in range</div>
                    )}
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="hdb-table w-full">
                    <thead><tr><th>{bucket === "week" ? "Week" : "Month"}</th>
                        <th className="text-right">Revenue</th><th className="text-right">Tax</th>
                        <th className="text-right">Bachat</th><th className="text-right">Invoices</th></tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.bucket}>
                                <td className="hdb-mono font-semibold">{r.bucket}</td>
                                <td className="hdb-num hdb-num-right font-bold">{fmt(r.revenue)}</td>
                                <td className="hdb-num hdb-num-right">{fmt(r.tax)}</td>
                                <td className="hdb-num hdb-num-right text-emerald-700">{fmt(r.bachat)}</td>
                                <td className="hdb-num hdb-num-right">{r.invoice_count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SavingsView({ data }) {
    const topItems = data?.top_items ?? [];
    const topClients = data?.top_clients ?? [];
    return (
        <div className="space-y-5">
            <div className="rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-6">
                <div className="text-xs uppercase tracking-widest opacity-80">Total Bachat given to customers</div>
                <div className="hdb-num font-black text-4xl mt-2">{fmt(data?.total_bachat ?? 0)}</div>
                <div className="text-xs opacity-80 mt-2">B2C invoices in this date range · cancelled invoices excluded</div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div>
                    <div className="hdb-label mb-2">Top items by Bachat given</div>
                    <div className="hdb-card overflow-hidden">
                        <table className="hdb-table w-full">
                            <thead><tr><th>#</th><th>Item</th><th className="text-right">Qty</th><th className="text-right">Bachat</th></tr></thead>
                            <tbody>
                                {topItems.map((r, i) => (
                                    <tr key={r.name}><td className="hdb-mono text-xs">{i + 1}</td>
                                        <td className="font-medium">{r.name}</td>
                                        <td className="hdb-num hdb-num-right">{fmtNum(r.qty)}</td>
                                        <td className="hdb-num hdb-num-right text-emerald-700 font-bold">{fmt(r.bachat)}</td>
                                    </tr>
                                ))}
                                {topItems.length === 0 && (
                                    <tr><td colSpan={4} className="text-center py-6 text-slate-400">No B2C invoices yet</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div>
                    <div className="hdb-label mb-2">Top clients by cumulative Bachat</div>
                    <div className="hdb-card overflow-hidden">
                        <table className="hdb-table w-full">
                            <thead><tr><th>#</th><th>Client</th><th className="text-right">Invoices</th><th className="text-right">Bachat</th></tr></thead>
                            <tbody>
                                {topClients.map((r, i) => (
                                    <tr key={r.name}><td className="hdb-mono text-xs">{i + 1}</td>
                                        <td className="font-medium">{r.name}</td>
                                        <td className="hdb-num hdb-num-right">{r.invoice_count}</td>
                                        <td className="hdb-num hdb-num-right text-emerald-700 font-bold">{fmt(r.bachat)}</td>
                                    </tr>
                                ))}
                                {topClients.length === 0 && (
                                    <tr><td colSpan={4} className="text-center py-6 text-slate-400">No B2C clients yet</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SalesMixView({ data }) {
    const rows = data?.rows ?? [];
    const avg = data?.averages ?? {};
    const b2b = avg.B2B ?? {};
    const b2c = avg.B2C ?? {};
    const maxCombined = Math.max(...rows.map((r) => r.combined), 1);

    const AvgCard = ({ type, a, accent }) => (
        <div className={`hdb-card p-5 border-t-4 ${accent}`} data-testid={`avg-card-${type.toLowerCase()}`}>
            <div className="flex items-center justify-between">
                <div className="hdb-label">Average {type} till now</div>
                <span className="hdb-tag hdb-tag-slate">{a.months_active ?? 0} mo active</span>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                    <div className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">Avg invoice value</div>
                    <div className="hdb-num text-2xl font-black text-slate-900 mt-1">{fmt(a.avg_invoice_value ?? 0)}</div>
                </div>
                <div>
                    <div className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">Avg monthly sales</div>
                    <div className="hdb-num text-2xl font-black text-slate-900 mt-1">{fmt(a.avg_monthly_revenue ?? 0)}</div>
                </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 gap-3 text-xs text-slate-600">
                <div>Total sales: <span className="hdb-num font-bold text-slate-900">{fmt(a.total_revenue ?? 0)}</span></div>
                <div>Invoices: <span className="hdb-num font-bold text-slate-900">{a.invoice_count ?? 0}</span></div>
                <div>Avg invoices/mo: <span className="hdb-num font-bold text-slate-900">{a.avg_invoices_per_month ?? 0}</span></div>
                <div>Distinct clients: <span className="hdb-num font-bold text-slate-900">{a.distinct_clients ?? 0}</span></div>
                <div className="col-span-2">Avg sales/client: <span className="hdb-num font-bold text-slate-900">{fmt(a.avg_revenue_per_client ?? 0)}</span></div>
            </div>
        </div>
    );

    const Delta = ({ value, marker }) => {
        if (!marker) return null;
        const diff = value - marker;
        if (Math.abs(diff) < 0.005) return <span className="text-[10px] text-slate-400">= avg</span>;
        const pct = ((diff / marker) * 100).toFixed(0);
        const up = diff > 0;
        return (
            <span className={`text-[10px] font-bold ${up ? "text-emerald-600" : "text-red-600"}`}
                  title={`vs avg monthly ${fmt(marker)}`}>
                {up ? "▲" : "▼"} {Math.abs(pct)}% vs avg
            </span>
        );
    };

    return (
        <div className="space-y-5" data-testid="salesmix-view">
            {/* Till-now average markers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <AvgCard type="B2B" a={b2b} accent="border-slate-800" />
                <AvgCard type="B2C" a={b2c} accent="border-emerald-500" />
            </div>

            {/* Monthly stacked bars */}
            <div className="hdb-card p-4">
                <div className="hdb-label mb-1">Monthly actual sales — B2B (dark) vs B2C (green)</div>
                <div className="text-[11px] text-slate-400 mb-3">
                    Dashed markers on the table below = till-now average monthly sales for each type.
                </div>
                <div className="space-y-2">
                    {rows.map((r) => {
                        const wB2B = (r.b2b_revenue / maxCombined) * 100;
                        const wB2C = (r.b2c_revenue / maxCombined) * 100;
                        return (
                            <div key={r.month} className="flex items-center gap-3" data-testid={`mix-bar-${r.month}`}>
                                <div className="text-xs hdb-mono text-slate-500 w-20">{r.month}</div>
                                <div className="flex-1 h-6 bg-slate-50 rounded overflow-hidden flex">
                                    <div className="h-full bg-slate-800" style={{ width: `${wB2B}%` }} title={`B2B ${fmt(r.b2b_revenue)}`}></div>
                                    <div className="h-full bg-emerald-500" style={{ width: `${wB2C}%` }} title={`B2C ${fmt(r.b2c_revenue)}`}></div>
                                </div>
                                <div className="text-xs hdb-num font-bold text-slate-700 w-24 text-right">{fmt(r.combined)}</div>
                                <div className="text-[10px] hdb-mono text-slate-400 w-14 text-right">{r.b2b_share}% B2B</div>
                            </div>
                        );
                    })}
                    {rows.length === 0 && (
                        <div className="text-center py-8 text-slate-400">No invoices in this range</div>
                    )}
                </div>
            </div>

            {/* Monthly table with vs-average markers */}
            <div className="overflow-x-auto">
                <table className="hdb-table w-full">
                    <thead><tr><th>Month</th>
                        <th className="text-right">B2B Total</th><th className="text-right">B2B Inv</th>
                        <th className="text-right">B2B Avg Inv</th>
                        <th className="text-right">B2C Total</th><th className="text-right">B2C Inv</th>
                        <th className="text-right">B2C Avg Inv</th>
                        <th className="text-right">Combined</th><th className="text-right">B2B Share</th></tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.month} data-testid={`mix-row-${r.month}`}>
                                <td className="hdb-mono font-semibold">{r.month}</td>
                                <td className="hdb-num hdb-num-right font-bold">
                                    {fmt(r.b2b_revenue)}
                                    <div><Delta value={r.b2b_revenue} marker={b2b.avg_monthly_revenue} /></div>
                                </td>
                                <td className="hdb-num hdb-num-right">{r.b2b_count}</td>
                                <td className="hdb-num hdb-num-right">{fmt(r.b2b_avg_invoice)}</td>
                                <td className="hdb-num hdb-num-right font-bold">
                                    {fmt(r.b2c_revenue)}
                                    <div><Delta value={r.b2c_revenue} marker={b2c.avg_monthly_revenue} /></div>
                                </td>
                                <td className="hdb-num hdb-num-right">{r.b2c_count}</td>
                                <td className="hdb-num hdb-num-right">{fmt(r.b2c_avg_invoice)}</td>
                                <td className="hdb-num hdb-num-right font-bold">{fmt(r.combined)}</td>
                                <td className="hdb-num hdb-num-right">{r.b2b_share}%</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={9} className="text-center py-8 text-slate-400">No invoices in this range</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
